import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/Table';
import { Input } from './ui/Input';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { Search, Save, Wallet } from 'lucide-react';
import { MOCK_USERS, INITIAL_CDS_TIERS, User } from '../data/mockData';
import { useAdjustments } from '../data/feesStore';
import { formatCurrency, cn } from '../lib/utils';
export function Draws() {
  const [users, setUsers] = useState<User[]>(MOCK_USERS);
  const [search, setSearch] = useState('');
  const [editForm, setEditForm] = useState<Record<string, number>>({});
  const [isEditing, setIsEditing] = useState(false);
  const allAdjustments = useAdjustments();
  // Calculate commissions and net balances
  const usersWithFinancials = useMemo(() => {
    return users.map(user => {
      let commission = 0;
      if (user.model === 'CDS') {
        const tier = INITIAL_CDS_TIERS.find(t => user.fundedMTD >= t.min && (t.max === null || user.fundedMTD <= t.max));
        commission = tier ? user.fundedMTD * tier.amount : 0;
      } else if (user.model === 'BPS' && user.fundedBySource && user.rates) {
        Object.entries(user.fundedBySource).forEach(([source, data]) => {
          const bps = user.rates![source] || 0;
          commission += data.volume * (bps / 10000);
        });
      }
      const currentDraw = editForm[user.id] !== undefined ? editForm[user.id] : user.currentMonthDraw;
      const userAdjustments = allAdjustments.filter(a => a.userId === user.id).reduce((sum, a) => sum + a.amount, 0);
      const netBalance = commission - user.drawBalance - currentDraw - userAdjustments;
      return {
        ...user,
        estimatedCommission: commission,
        currentDraw,
        userAdjustments,
        netBalance
      };
    });
  }, [users, editForm, allAdjustments]);
  const filteredUsers = usersWithFinancials.filter(u => u.name.toLowerCase().includes(search.toLowerCase()) || u.title.toLowerCase().includes(search.toLowerCase()));
  const startEditing = () => {
    const initialForm: Record<string, number> = {};
    users.forEach(u => {
      initialForm[u.id] = u.currentMonthDraw;
    });
    setEditForm(initialForm);
    setIsEditing(true);
  };
  const cancelEditing = () => {
    setIsEditing(false);
    setEditForm({});
  };
  const saveDraws = () => {
    setUsers(users.map(u => ({
      ...u,
      currentMonthDraw: editForm[u.id] ?? u.currentMonthDraw
    })));
    setIsEditing(false);
  };
  const handleDrawChange = (userId: string, value: string) => {
    const numValue = parseInt(value, 10);
    setEditForm(prev => ({
      ...prev,
      [userId]: isNaN(numValue) ? 0 : numValue
    }));
  };
  const totalDraws = usersWithFinancials.reduce((sum, u) => sum + u.currentDraw, 0);
  const totalOutstandingBalances = usersWithFinancials.reduce((sum, u) => sum + u.drawBalance, 0);
  return <div className="space-y-6" data-id="element-115">
      <div className="flex items-center justify-between pb-2" data-id="element-116">
        <div data-id="element-117">
          <h1 className="text-2xl font-semibold text-slate-900" data-id="element-118">
            Draw Management
          </h1>
          <p className="text-sm text-slate-500" data-id="element-119">
            Manage monthly draws and track outstanding balances.
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2" data-id="element-120">
        <Card className="shadow-sm border-slate-200/60 rounded-xl" data-id="element-121">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-122">
            <CardTitle className="text-sm font-medium text-slate-600" data-id="element-123">
              Total Current Month Draws
            </CardTitle>
            <Wallet className="h-4 w-4 text-blue-600" data-id="element-124" />
          </CardHeader>
          <CardContent data-id="element-125">
            <div className="text-2xl font-bold text-slate-900" data-id="element-126">
              {formatCurrency(totalDraws)}
            </div>
            <p className="text-xs text-slate-500 mt-1" data-id="element-127">
              Issued across all users this month
            </p>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-slate-200/60 rounded-xl" data-id="element-128">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-129">
            <CardTitle className="text-sm font-medium text-slate-600" data-id="element-130">
              Total Outstanding Balances
            </CardTitle>
            <Wallet className="h-4 w-4 text-red-600" data-id="element-131" />
          </CardHeader>
          <CardContent data-id="element-132">
            <div className="text-2xl font-bold text-slate-900" data-id="element-133">
              {formatCurrency(totalOutstandingBalances)}
            </div>
            <p className="text-xs text-slate-500 mt-1" data-id="element-134">
              Carried over from previous months
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="flex flex-col h-[calc(100vh-16rem)] shadow-sm border-slate-200/60 bg-white rounded-xl" data-id="element-135">
        <CardHeader className="border-b border-slate-100 shrink-0 flex flex-row items-center justify-between pb-4" data-id="element-136">
          <div data-id="element-137">
            <CardTitle className="flex items-center" data-id="element-138">
              <Wallet className="mr-2 h-5 w-5 text-blue-500" data-id="element-139" />
              Draw Management
            </CardTitle>
            <CardDescription data-id="element-140">
              Manage monthly draws and track outstanding balances.
            </CardDescription>
          </div>
          <div className="flex items-center gap-4" data-id="element-141">
            <div className="relative w-64" data-id="element-142">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" data-id="element-143" />
              <Input placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 bg-slate-50 border-slate-200 focus:bg-white" data-id="element-144" />
            </div>
            {!isEditing ? <Button onClick={startEditing} className="bg-slate-900 text-white hover:bg-slate-800" data-id="element-145">
                Edit Draws
              </Button> : <div className="flex gap-2" data-id="element-146">
                <Button variant="outline" onClick={cancelEditing} data-id="element-147">
                  Cancel
                </Button>
                <Button onClick={saveDraws} className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm" data-id="element-148">
                  <Save className="mr-2 h-4 w-4" data-id="element-149" /> Save Changes
                </Button>
              </div>}
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto p-0" data-id="element-150">
          <Table data-id="element-151">
            <TableHeader className="sticky top-0 bg-slate-50/90 backdrop-blur-sm shadow-sm z-10 border-b border-slate-200" data-id="element-152">
              <TableRow className="hover:bg-transparent" data-id="element-153">
                <TableHead className="pl-6 font-semibold text-slate-600" data-id="element-154">
                  User
                </TableHead>
                <TableHead className="text-right font-semibold text-slate-600" data-id="element-155">
                  Est. Commission
                </TableHead>
                <TableHead className="text-right font-semibold text-slate-600" data-id="element-156">
                  Previous Balance
                </TableHead>
                <TableHead className="text-right font-semibold text-slate-600" data-id="element-157">
                  Current Draw
                </TableHead>
                <TableHead className="text-right font-semibold text-slate-600" data-id="element-158">
                  Adjustments
                </TableHead>
                <TableHead className="text-right pr-6 font-semibold text-slate-600" data-id="element-159">
                  Net Balance
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-id="element-160">
              {filteredUsers.map(user => <TableRow key={user.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-161">
                  <TableCell className="pl-6 py-3" data-id="element-162">
                    <div className="flex items-center gap-4" data-id="element-163">
                      <img src={user.avatar} alt={user.name} className="h-10 w-10 rounded-full bg-slate-200 shadow-sm border border-slate-100" data-id="element-164" />
                      <div data-id="element-165">
                        <div className="font-semibold text-sm text-slate-900" data-id="element-166">
                          {user.name}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5" data-id="element-167">
                          <Badge variant={user.model === 'BPS' ? 'default' : 'secondary'} className={cn('text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-md', user.model === 'BPS' ? 'bg-slate-900 text-white' : 'bg-blue-100 text-blue-700 hover:bg-blue-100')} data-id="element-168">
                            {user.model}
                          </Badge>
                          <span className="text-xs text-slate-500 font-medium" data-id="element-169">
                            {user.title}
                          </span>
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium text-slate-700" data-id="element-170">
                    {formatCurrency(user.estimatedCommission)}
                  </TableCell>
                  <TableCell className="text-right font-medium text-slate-500" data-id="element-171">
                    {user.drawBalance > 0 ? <span className="text-red-600" data-id="element-172">
                        -{formatCurrency(user.drawBalance)}
                      </span> : <span className="text-slate-400" data-id="element-173">—</span>}
                  </TableCell>
                  <TableCell className="text-right" data-id="element-174">
                    {isEditing ? <div className="flex items-center justify-end" data-id="element-175">
                        <span className="mr-2 text-slate-400" data-id="element-176">$</span>
                        <Input type="number" className="w-24 text-right font-semibold bg-white border-blue-200 focus:border-blue-500 focus:ring-blue-500 h-8" value={editForm[user.id] ?? 0} onChange={e => handleDrawChange(user.id, e.target.value)} data-id="element-177" />
                      </div> : <span className="font-semibold text-slate-700" data-id="element-178">
                        {user.currentDraw > 0 ? formatCurrency(user.currentDraw) : <span className="text-slate-400" data-id="element-179">—</span>}
                      </span>}
                  </TableCell>
                  <TableCell className="text-right font-medium text-slate-500" data-id="element-180">
                    {user.userAdjustments > 0 ? <span className="text-red-600" data-id="element-181">
                        -{formatCurrency(user.userAdjustments)}
                      </span> : <span className="text-slate-400" data-id="element-182">—</span>}
                  </TableCell>
                  <TableCell className="text-right pr-6" data-id="element-183">
                    <span className={cn('font-bold', user.netBalance < 0 ? 'text-red-600' : user.netBalance > 0 ? 'text-emerald-600' : 'text-slate-500')} data-id="element-184">
                      {formatCurrency(user.netBalance)}
                    </span>
                    {user.netBalance < 0 && <div className="text-[10px] text-red-500 font-medium mt-0.5" data-id="element-185">
                        Carries over
                      </div>}
                  </TableCell>
                </TableRow>)}
              {filteredUsers.length === 0 && <TableRow data-id="element-186">
                  <TableCell colSpan={6} className="h-24 text-center text-slate-500" data-id="element-187">
                    No users found matching "{search}"
                  </TableCell>
                </TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>;
}